import { useEffect, useRef } from "react";

interface UseHoldKeyOptions {
  /** `KeyboardEvent.code` of each key that can be held, e.g. ["AltLeft", "AltRight"]. */
  codes: readonly string[];
  enabled?: boolean;
  onHoldStart: () => void;
  onHoldEnd: () => void;
  /**
   * Another key was pressed while holding — the key was being used as a
   * modifier (Option+e for é), not held on its own.
   */
  onHoldCancel?: () => void;
  /** Return false to ignore a press, e.g. while the user types in another field. */
  isAllowedTarget?: (target: EventTarget | null) => boolean;
  /**
   * How long the key must be held before it counts. A shorter press, or one
   * that turns into a shortcut first (Option+← in a text editor), never starts
   * the hold and calls nothing. 0 starts on keydown.
   */
  holdDelayMs?: number;
}

type KeyDownAction = "ignore" | "swallow" | "start" | "cancel";

const classifyKeyDown = (
  event: KeyboardEvent,
  codes: readonly string[],
  heldCode: string | null,
  isAllowedTarget?: (target: EventTarget | null) => boolean,
): KeyDownAction => {
  if (heldCode !== null) return event.code === heldCode ? "swallow" : "cancel";
  if (!codes.includes(event.code)) return "ignore";
  if (event.repeat) return "swallow";
  // AltGr on Windows layouts arrives as Ctrl+Alt and types characters.
  if (event.ctrlKey || event.metaKey) return "ignore";
  if (isAllowedTarget && !isAllowedTarget(event.target)) return "ignore";
  return "start";
};

/**
 * Press-and-hold of a single key, page-wide, for push-to-talk style controls.
 *
 * - Auto-repeat keydowns while held are ignored.
 * - A press with Ctrl/Meta also down is ignored (Windows AltGr).
 * - Losing the window (switching app or tab) counts as releasing, because the
 *   keyup will never arrive.
 * - With `holdDelayMs`, the hold starts only once the key has been down that
 *   long; releasing or pressing another key sooner ends it silently.
 *
 * Browsers only deliver keys the OS passes on: Fn/🌐 never reaches a web page,
 * and nothing works while the tab isn't focused.
 */
export const useHoldKey = ({
  codes,
  enabled = true,
  onHoldStart,
  onHoldEnd,
  onHoldCancel,
  isAllowedTarget,
  holdDelayMs = 0,
}: UseHoldKeyOptions): void => {
  const heldCodeRef = useRef<string | null>(null);
  /** True once `onHoldStart` ran for the current press. */
  const startedRef = useRef(false);
  const delayTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Latest handlers without re-binding the window listeners on every render.
  const handlersRef = useRef({
    onHoldStart,
    onHoldEnd,
    onHoldCancel,
    isAllowedTarget,
  });
  handlersRef.current = {
    onHoldStart,
    onHoldEnd,
    onHoldCancel,
    isAllowedTarget,
  };

  useEffect(() => {
    if (!enabled) return;

    const clearDelayTimer = () => {
      if (delayTimerRef.current === null) return;
      clearTimeout(delayTimerRef.current);
      delayTimerRef.current = null;
    };

    const begin = () => {
      delayTimerRef.current = null;
      startedRef.current = true;
      handlersRef.current.onHoldStart();
    };

    const release = (cancelled: boolean) => {
      if (heldCodeRef.current === null) return;
      heldCodeRef.current = null;
      clearDelayTimer();
      // Let go before the delay: the hold never started, so nothing to end.
      if (!startedRef.current) return;
      startedRef.current = false;
      const { onHoldEnd: end, onHoldCancel: cancel } = handlersRef.current;
      if (cancelled && cancel) cancel();
      else end();
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      const action = classifyKeyDown(
        event,
        codes,
        heldCodeRef.current,
        handlersRef.current.isAllowedTarget,
      );
      if (action === "cancel") release(true);
      if (action === "swallow" || action === "start") event.preventDefault();
      if (action !== "start") return;
      heldCodeRef.current = event.code;
      if (holdDelayMs > 0)
        delayTimerRef.current = setTimeout(begin, holdDelayMs);
      else begin();
    };

    const handleKeyUp = (event: KeyboardEvent) => {
      if (heldCodeRef.current === null || event.code !== heldCodeRef.current)
        return;
      // Stops Windows browsers from moving focus to the menu on an Alt release.
      event.preventDefault();
      release(false);
    };

    const handleBlur = () => release(false);
    const handleVisibilityChange = () => {
      if (document.hidden) release(false);
    };

    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);
    window.addEventListener("blur", handleBlur);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
      window.removeEventListener("blur", handleBlur);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      // Unmounting: the owner tears its own feature down, so just forget the hold.
      clearDelayTimer();
      heldCodeRef.current = null;
      startedRef.current = false;
    };
  }, [codes, enabled, holdDelayMs]);
};
